import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import HttpBackend from "i18next-http-backend";
import {
  supportedLngs,
  fallbackLng,
  defaultNS,
  coreNamespaces,
  i18nLoadStrategy,
} from "./i18n.config";
import { Logger } from "@/lib/app-log.js";

/**
 * Vite does not reliably emit chunks for `import(\`@locales/${lng}/${ns}.json\`)`,
 * so new namespace files never load. Glob registers every JSON upfront as lazy loaders.
 *
 * Runtime: only `coreNamespaces` preload at init; feature ns load on demand.
 * Locale payloads are Vite hashed chunks under `/assets/` (immutable cache).
 * `dist/locales/` from viteStaticCopy is a static copy, not the primary loader path.
 */
const localeModuleLoaders = {
  ...import.meta.glob("@locales/*/*.json"),
  ...import.meta.glob("@/locales/*/*.json"),
};
const NAMESPACE_ALIASES = {};

/** All JSON basenames — used for loader resolution, not for eager init. */
export const discoveredNamespaces = [...new Set(
  Object.keys(localeModuleLoaders)
    .map((path) => {
      const match = path.replace(/\\/g, "/").match(/\/([^/]+)\.json$/);
      return match ? match[1] : null;
    })
    .filter(Boolean)
)];

function resolveLocaleLoader(lng, ns) {
  const normalizedNs = NAMESPACE_ALIASES[ns] || ns;
  const needle = `${lng}/${normalizedNs}.json`;
  for (const path of Object.keys(localeModuleLoaders)) {
    const normalized = path.replace(/\\/g, "/");
    if (normalized.endsWith(needle)) {
      return localeModuleLoaders[path];
    }
  }
  return null;
}

void i18n
  .use(HttpBackend)
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    fallbackLng,
    supportedLngs,
    load: i18nLoadStrategy,
    cleanCode: true,
    // Shell only — feature namespaces load when useTranslation / loadNamespaces requests them.
    ns: coreNamespaces,
    defaultNS,
    interpolation: {
      escapeValue: false,
    },
    detection: {
      order: ["localStorage", "navigator", "htmlTag"],
      caches: ["localStorage"],
    },
    react: {
      // Shell and list pages paint immediately; tables keep the overlay spinner.
      // Late islands pass LATE_I18N_OPTIONS so only that subtree waits.
      useSuspense: false,
    },
    backend: {
      loadPath: "{{lng}}/{{ns}}",
      request: (options, url, payload, callback) => {
        const [lng, ns] = url.split("/");
        const load = resolveLocaleLoader(lng, ns);
        if (!load) {
          Logger.error(`Failed to load locale (no module): ${lng}/${ns}`);
          callback(null, { status: 404, data: {} });
          return;
        }
        load()
          .then((module) => {
            callback(null, { status: 200, data: module.default });
          })
          .catch((error) => {
            Logger.error(`Failed to load locale: ${lng}/${ns}`, { error: error?.message });
            callback(null, { status: 404, data: {} });
          });
      },
    },
  });

export default i18n;
